import type { SliderModelValue } from '@travelata-libs/ui-kit';
import { formatMoney, pluralize } from '@travelata-libs/utils';
import { BUDGET_FILTER_PRESETS, BUDGET_FILTER_PRESETS_LIMIT, BUDGET_FILTER_STEP } from '../config/filters';
import { BUDGET_TOURISTS_PLURAL_FORMS, SERP_BUDGET_FILTER_TEXTS } from '../config/texts';
import type { FiltersModel, SerpBudgetFilterGroupView } from '../model/types';

const WHITESPACE_RE = /\s/g;
const NON_DIGIT_RE = /\D/g;
const DIGIT_RE = /\d/;
const NBSP = ' ';

export function formatBudgetAmount(value: number): string {
  return formatMoney(value, null).replaceAll(WHITESPACE_RE, NBSP);
}

export function parseBudgetAmount(value: string): number {
  return Number(value.replaceAll(NON_DIGIT_RE, ''));
}

export function countBudgetDigits(value: string): number {
  return value.replaceAll(NON_DIGIT_RE, '').length;
}

export function findBudgetCaret(value: string, digitsBeforeCaret: number): number {
  if (digitsBeforeCaret <= 0) return 0;

  let digits = 0;

  for (const [index, char] of value.split('').entries()) {
    if (DIGIT_RE.test(char)) digits += 1;
    if (digits === digitsBeforeCaret) return index + 1;
  }

  return value.length;
}

export function formatBudgetFilterLabel(budget: FiltersModel['budget']): string {
  const { selectedPrefix, from, upTo, currency } = SERP_BUDGET_FILTER_TEXTS;
  const parts: string[] = [selectedPrefix];

  if (budget.min) parts.push(`${from} ${formatBudgetAmount(budget.min)}`);
  if (budget.max) parts.push(`${upTo} ${formatBudgetAmount(budget.max)}`);

  return `${parts.join(' ')}${NBSP}${currency}`;
}

export function toBudgetSliderBounds(view: SerpBudgetFilterGroupView): SliderModelValue {
  return {
    min: Math.floor(view.minPrice / BUDGET_FILTER_STEP),
    max: Math.ceil(view.maxPrice / BUDGET_FILTER_STEP),
  };
}

export function toBudgetSliderValue(budget: FiltersModel['budget'], bounds: SliderModelValue): SliderModelValue {
  return {
    min: budget.min ? clampToBounds(budget.min / BUDGET_FILTER_STEP, bounds) : bounds.min,
    max: budget.max ? clampToBounds(budget.max / BUDGET_FILTER_STEP, bounds) : bounds.max,
  };
}

export function fromBudgetSliderValue(
  value: SliderModelValue,
  bounds: SliderModelValue,
  view: SerpBudgetFilterGroupView,
): FiltersModel['budget'] {
  return {
    min: value.min <= bounds.min ? 0 : Math.min(value.min * BUDGET_FILTER_STEP, view.maxPrice),
    max: value.max >= bounds.max ? 0 : Math.max(value.max * BUDGET_FILTER_STEP, view.minPrice),
  };
}

export function getBudgetPresets(minPrice: number, maxPrice: number): number[] {
  return BUDGET_FILTER_PRESETS.filter((preset) => preset >= minPrice && preset <= maxPrice).slice(
    0,
    BUDGET_FILTER_PRESETS_LIMIT,
  );
}

export function toggleBudgetPreset(budget: FiltersModel['budget'], preset: number): FiltersModel['budget'] {
  if (budget.max === preset) return { min: budget.min, max: 0 };

  return { min: budget.min > preset ? 0 : budget.min, max: preset };
}

export function formatBudgetTourists(count: number): string {
  if (count <= 0) return '';

  return `${SERP_BUDGET_FILTER_TEXTS.priceFor} ${pluralize(BUDGET_TOURISTS_PLURAL_FORMS, count)}`;
}

function clampToBounds(value: number, bounds: SliderModelValue): number {
  return Math.min(Math.max(value, bounds.min), bounds.max);
}
